/**
 * Owns the session countdown, the warning threshold, and the heartbeat poll.
 * No imports from Http — the transport is injected at start() to avoid a
 * circular import.
 */
const WARN_DEFAULT = 300

let expiresAtMs = null
let warnAt = WARN_DEFAULT
let tickId = null
let heartbeatId = null
let transport = null
const listeners = new Set()

function emit() {
  const secondsLeft =
    expiresAtMs === null ? null : Math.max(0, Math.round((expiresAtMs - Date.now()) / 1000))

  const state = {
    secondsLeft,
    warnAt,
    showWarning: secondsLeft !== null && secondsLeft > 0 && secondsLeft <= warnAt
  }

  listeners.forEach((fn) => fn(state))
  return secondsLeft
}

function onVisible() {
  if (document.visibilityState === "visible") emit()
}

function stopTimers() {
  if (tickId) clearInterval(tickId)
  if (heartbeatId) clearInterval(heartbeatId)

  tickId = null
  heartbeatId = null

  document.removeEventListener("visibilitychange", onVisible)
}

export function subscribe(fn) {
  listeners.add(fn)
  emit()

  return () => listeners.delete(fn)
}

/**
 * Anchor on an absolute local timestamp derived from the server's TTL in
 * seconds. Never parse token_expiry against Date.now().
 */
export function applyTtl(ttlSeconds, warnSeconds) {
  if (Number.isFinite(ttlSeconds)) {
    expiresAtMs = Date.now() + (ttlSeconds * 1000)
    localStorage.setItem("tokenExpiry", String(expiresAtMs))
  }

  if (Number.isFinite(warnSeconds)) warnAt = warnSeconds

  emit()
}

export function start(injected) {
  transport = injected
  stopTimers()

  if (expiresAtMs === null) {
    const stored = Number(localStorage.getItem("tokenExpiry"))

    if (Number.isFinite(stored) && stored > 0) {
      expiresAtMs = stored
    }
  }

  tickId = setInterval(() => {
    const left = emit()

    if (left === 0) {
      transport?.onExpire?.()
    }
  }, 1000)

  document.addEventListener("visibilitychange", onVisible)

  heartbeatId = setInterval(() => {
    transport?.heartbeat?.().catch(() => {})
  }, 60000)

  emit()
}

export function stop() {
  stopTimers()
  expiresAtMs = null
  emit()
}

export async function extend() {
  if (!transport?.extend) return false

  try {
    const data = await transport.extend()

    if (Number.isFinite(data?.ttl_seconds)) {
      applyTtl(data.ttl_seconds)
    }

    return true
  } catch {
    return false
  }
}

export default { subscribe, applyTtl, start, stop, extend }